"use client"

import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTranslation } from "../../hooks/use-translation"
import { ArrowLeft } from "lucide-react"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { getArticleById } from "../../redux/slices/blogSlice"
import { formatDate } from "../../utils/timestamp"

export default function BlogArticlePage() {
  const { articleId } = useParams<{ articleId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { articles, isLoading } = useAppSelector((state) => state.blog)
  const [article, setArticle] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (articleId) {
      // First check if the article is already in the Redux store
      const existingArticle = articles.find((a) => a.id === articleId)

      if (existingArticle) {
        setArticle(existingArticle)
      } else {
        // If not, fetch it from the API
        dispatch(getArticleById(articleId))
          .unwrap()
          .then((fetchedArticle) => {
            setArticle(fetchedArticle)
          })
          .catch((err) => {
            setError(err.toString())
          })
      }
    }
  }, [articleId, articles, dispatch])

  // When articles in the store change, check if our article is there
  useEffect(() => {
    if (articleId && !article) {
      const existingArticle = articles.find((a) => a.id === articleId)
      if (existingArticle) {
        setArticle(existingArticle)
      }
    }
  }, [articles, articleId, article])

  if (isLoading && !article) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="my-3 p-4 rounded-4 text-white"
        style={{
          background: "rgba(220, 53, 69, 0.2)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(220, 53, 69, 0.3)",
        }}
      >
        {error}
      </div>
    )
  }

  if (!article) {
    return (
      <div
        className="my-3 p-4 rounded-4 text-white"
        style={{
          background: "rgba(255, 193, 7, 0.2)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 193, 7, 0.3)",
        }}
      >
        {t("articleNotFound")}
      </div>
    )
  }

  return (
    <>
      {/* Mobile Layout */}
      <div className="d-md-none d-flex flex-column" style={{ height: "100vh" }}>
        <div
          className="position-fixed top-0 start-0 end-0"
          style={{
            height: "70px",
            background: "#101E46",
            zIndex: 1030,
          }}
        >
          <div className="d-flex align-items-center gap-3 h-100 px-3">
            <button className="btn-back-circle flex-shrink-0" onClick={() => navigate(-1)}>
              <ArrowLeft size={20} />
            </button>
            <h1
              className="text-white fs-5 fw-bold mb-0 flex-grow-1"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {article.title}
            </h1>
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto">
          <div className="p-4">
            <div
              className="mb-4 rounded-4 overflow-hidden"
              style={{
                background: "#101E46",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              {article.thumbnail && (
                <div className="text-center p-3" style={{ background: "rgba(255, 255, 255, 0.05)" }}>
                  <img
                    src={article.thumbnail || "/placeholder.svg?height=400&width=800"}
                    alt={article.title}
                    className="img-fluid rounded"
                    style={{ maxHeight: "400px" }}
                  />
                </div>
              )}
              <div className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <small className="text-white opacity-75">{formatDate(article.date)}</small>
                </div>

                <div className="mb-4">
                  <p className="text-white fs-6 fw-medium opacity-90">{article.preview}</p>
                </div>

                <div className="article-content text-white opacity-85">
                  {article.content.split("\n").map((paragraph: string, index: number) =>
                    paragraph.trim() ? (
                      <p key={index} className="mb-3">
                        {paragraph}
                      </p>
                    ) : (
                      <br key={index} />
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="d-none d-md-flex flex-column py-3">
        <div className="mb-3 d-flex align-items-center gap-3">
          <button className="btn-back-circle" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="fs-3 fw-bold mb-0 text-white">{article.title}</h1>
        </div>

        <div
          className="rounded-4 overflow-hidden"
          style={{
            background: "#101E46",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          {article.thumbnail && (
            <div className="text-center p-4" style={{ background: "rgba(255, 255, 255, 0.05)" }}>
              <img
                src={article.thumbnail || "/placeholder.svg?height=400&width=800"}
                alt={article.title}
                className="img-fluid rounded"
                style={{ maxHeight: "500px" }}
              />
            </div>
          )}
          <div className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <small className="text-white opacity-75">{formatDate(article.date)}</small>
            </div>

            <div className="mb-4">
              <p className="text-white fs-5 fw-medium opacity-90">{article.preview}</p>
            </div>

            <div className="article-content text-white opacity-85">
              {article.content.split("\n").map((paragraph: string, index: number) =>
                paragraph.trim() ? (
                  <p key={index} className="mb-3">
                    {paragraph}
                  </p>
                ) : (
                  <br key={index} />
                ),
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
